import { ReactComponent as WalletIcon } from 'assets/icons/icon-wallet.svg';
import Card from 'components/Card/Card';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import GoBackButton from 'components/GoBackButton/GoBackButton';
import Page from 'components/Page';
import PageIcon from 'components/PageIcon/PageIcon';
import ResetAndSubmitButton from 'components/ResetAndSubmitButton/ResetAndSubmitButton';
import { useSearchBusinessUnitsQuery } from 'graphql/queries/SearchBusinessUnits.generated';
import { useSearchCurrencyBooksQuery } from 'graphql/queries/SearchCurrencyBooks.generated';
import { Product_Types_Enum_Enum } from 'graphql/schemaTypes.generated';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'routeMap';
import {
  useAsyncSearchSelect,
  useValueDidUpdate,
  useWrappedUrqlMutation,
  useSearchParams,
} from 'toolbox';
import { useCreateBankAccountMutation } from './CreateBankAccount.generated';
import { CreateBankAccountForm } from './forms';
import { useGetOrgShortNameQuery } from './GetOrgShortName.generated';
// import { useGetOrganisationQuery } from 'components/Admin/orgs/queries/GetOrganisation.generated';

const makeAccountId = (short_name: string, description: string) => {
  return `${short_name.toUpperCase()}${description
    .toUpperCase()
    .replace(/[\W_]+/g, '') || ''}`;
};

export const CreateBankAccount = () => {
  const navigate = useNavigate();
  const params = useSearchParams<{
    orgId: number;
    buId: number;
    cbId: number;
    ccyId: number;
  }>();

  const create = useWrappedUrqlMutation(useCreateBankAccountMutation, {
    onSuccess: (result) => {
      toast.info('Created bank account successfully.');
      navigate.viewBankAccountById({
        params: {
          account_id: result.insert_bank_accounts_one.account_id,
        },
      });
    },
    onError: (error) => {
      toast.error('Could not create bank account.');
      console.log(error);
    },
  });

  return (
    <Page icon={<PageIcon Icon={WalletIcon} />} title="New Bank Account">
      <GoBackButton>Back</GoBackButton>
      <CreateBankAccountForm.Wrapper
        onSubmit={(values) => {
          // console.log(values);
          create.dispatch({
            bank_id: values.bank_id,
            currencyId: values.currencyId,
            currency_book_id: values.currency_book_id,
            product_type: values.product_type,
            account_description: values.account_description,
            fixed_maturity_date:
              values.product_type === 'FIXED'
                ? values.fixed_maturity_date
                : null,
            notice_days:
              values.product_type === 'NOTICE' ? values.notice_days : 0,
            opening_interest_rate: values.opening_interest_rate,
            account_id: values.account_id,
            organisation_id: values.organisation_id,
          });
        }}
        initialValues={{
          organisation_id: Number(params?.orgId),
          business_unit_id: Number(params?.buId),
          currency_book_id: Number(params?.cbId),
          // TODO
          currencyId: Number(params?.ccyId),
          product_type: Product_Types_Enum_Enum.Call,
          opening_interest_rate: '0.0',
          notice_days: 30,
          fixed_maturity_date: new Date().toISOString().substring(0, 10),
        }}
      >
        <Card footer={<ResetAndSubmitButton />}>
          <ErrorMessage {...create} />
          <CreateBankAccountForm.Inputs.bank_id label="Bank" />
          <OrganisationBusinessUnitCurrencyBookSelectGroup
            orgId={params?.orgId}
          />
          <CreateBankAccountForm.Inputs.product_type label="Account Type" />
          <CreateBankAccountForm.Consumer>
            {({ values }) => {
              switch (values.product_type) {
                case 'FIXED':
                  return (
                    <CreateBankAccountForm.Inputs.fixed_maturity_date label="Fixed Maturity Date" />
                  );
                case 'NOTICE':
                  return (
                    <CreateBankAccountForm.Inputs.notice_days label="Notice Days" />
                  );
                default:
                  return null;
              }
            }}
          </CreateBankAccountForm.Consumer>
          <CreateBankAccountForm.Inputs.opening_interest_rate label="Interest Rate (%AER)" />
          <CreateBankAccountForm.Inputs.account_description label="Account Description" />
          <AccountIdInput />
        </Card>
      </CreateBankAccountForm.Wrapper>
    </Page>
  );
};

const AccountIdInput = () => {
  const { values, setValues } = CreateBankAccountForm.useContext();

  const [result] = useGetOrgShortNameQuery({
    pause: !values.organisation_id,
    variables: {
      id: values.organisation_id,
    },
  });

  const short_name = result?.data?.organisations_by_pk?.short_name;

  useEffect(() => {
    if (short_name && values.account_description) {
      setValues({
        ...values,
        account_id: makeAccountId(short_name, values.account_description),
      });
    }
  }, [short_name, values.account_description]);
  return (
    <CreateBankAccountForm.Inputs.account_id disabled label="Account ID" />
  );
};

const OrganisationBusinessUnitCurrencyBookSelectGroup = ({ orgId }) => {
  const { values, setValues } = CreateBankAccountForm.useContext();

  const businessUnitSearchOptions = useAsyncSearchSelect({
    useQuery: useSearchBusinessUnitsQuery,
    makeVariablesFromInput: (input) => ({
      limit: 10,
      where: {
        _and: [
          { name: { _ilike: `%${input}%` } },
          { organisation_id: { _eq: values?.organisation_id } },
        ],
      },
      order_by: [],
    }),
    resultAccessor: (result) => {
      return result?.data?.business_units;
    },
    valueAccessor: ({ id }) => id,
  });

  const currencyBookSearchOptions = useAsyncSearchSelect({
    useQuery: useSearchCurrencyBooksQuery,
    makeVariablesFromInput: (input) => ({
      limit: 10,
      where: {
        _and: [
          { name: { _ilike: `%${input}%` } },
          { business_unit_id: { _eq: values?.business_unit_id } },
        ],
      },
      order_by: [],
    }),
    resultAccessor: (result) => {
      return result?.data?.currency_books;
    },
    valueAccessor: ({ id }) => id,
  });

  useValueDidUpdate(values.organisation_id, (prevValue) => {
    if (!prevValue) return;
    setValues({ ...values, business_unit_id: null, currency_book_id: null });
  });

  useValueDidUpdate(values.business_unit_id, (prevValue) => {
    if (!prevValue) return;
    setValues({ ...values, currency_book_id: null });
  });

  useEffect(() => {
    const currencyId =
      currencyBookSearchOptions.options.find(
        (option) => option.id === values.currency_book_id,
      )?.currency_id || null;
    if (currencyId) {
      setValues({
        ...values,
        currencyId,
      });
    }
  }, [values.currency_book_id]);

  return (
    <>
      <CreateBankAccountForm.Inputs.organisation_id label="Organisation" />
      <CreateBankAccountForm.Inputs.business_unit_id
        label="Business Unit"
        disabled={!values.organisation_id}
        labelAccessor={({ name }) => name}
        valueAccessor={({ id }) => id}
        {...businessUnitSearchOptions}
      />
      <CreateBankAccountForm.Inputs.currency_book_id
        label="Currency Book"
        disabled={!values.business_unit_id}
        labelAccessor={({ name }) => name}
        valueAccessor={({ id }) => id}
        {...currencyBookSearchOptions}
      />
      <CreateBankAccountForm.Inputs.currencyId disabled label="Currency" />
    </>
  );
};

export default CreateBankAccount;
